import { Link } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  Phone,
  Droplets,
  FlaskConical,
  Megaphone,
  Beaker,
  Check,
  Factory,
  Fuel,
  Truck,
  Plane,
  Anchor,
  HardHat,
  Landmark,
  Siren,
  Building2,
  Leaf,
} from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { aboutContent } from "../mock";

const iconMap = { Droplets, FlaskConical, Megaphone, Beaker };

const industryIcons = {
  "Industrial Manufacturing": Factory,
  "Oil & Gas": Fuel,
  "Transportation & Trucking": Truck,
  Aviation: Plane,
  "Marine & Port Operations": Anchor,
  Construction: HardHat,
  "Municipal & Public Works": Landmark,
  "Fire Departments & Emergency Response": Siren,
  "Commercial Facilities": Building2,
  "Environmental Remediation": Leaf,
};

export default function About() {
  const c = aboutContent;
  return (
    <div>
      <PageHero kicker="Market Street Ventures" title="About Us" image={c.image} />

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-[1000px] px-6">
          <p className="font-heading uppercase tracking-[0.35em] text-[#1b75bc] text-sm mb-3 text-center">
            Company Overview
          </p>
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.06em] text-[#1a2540] mb-6 text-center">
            {c.heading}
          </h2>
          <div className="w-14 h-[3px] bg-[#1b75bc] mx-auto mb-10" />
          <p className="text-[#6a6a6a] leading-relaxed text-center max-w-3xl mx-auto">
            {c.overview}
          </p>

          {/* Corporate HQ card */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div className="group relative flex items-start gap-4 bg-white rounded-2xl border border-gray-100 p-6 overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-[#1b75bc]/30 hover:shadow-[0_18px_36px_-20px_rgba(15,41,74,0.35)] hover:-translate-y-1 transition-all duration-300">
              <span className="absolute left-0 top-0 h-full w-1 bg-transparent group-hover:bg-[#1b75bc] transition-colors duration-300" />
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1b75bc] to-[#12507f] flex items-center justify-center shrink-0 shadow-[0_8px_16px_-6px_rgba(27,117,188,0.55)] transition-transform duration-300 group-hover:scale-110">
                <MapPin size={20} className="text-white" />
              </div>
              <div>
                <p className="font-heading uppercase tracking-[0.1em] text-sm text-[#1a2540] mb-1">
                  Corporate Headquarters
                </p>
                <p className="text-sm text-[#6a6a6a] leading-relaxed">
                  {c.hq.name}
                  <br />
                  {c.hq.line1}
                  <br />
                  {c.hq.line2}
                </p>
              </div>
            </div>
            <div className="group relative flex items-start gap-4 bg-white rounded-2xl border border-gray-100 p-6 overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-[#1b75bc]/30 hover:shadow-[0_18px_36px_-20px_rgba(15,41,74,0.35)] hover:-translate-y-1 transition-all duration-300">
              <span className="absolute left-0 top-0 h-full w-1 bg-transparent group-hover:bg-[#1b75bc] transition-colors duration-300" />
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1b75bc] to-[#12507f] flex items-center justify-center shrink-0 shadow-[0_8px_16px_-6px_rgba(27,117,188,0.55)] transition-transform duration-300 group-hover:scale-110">
                <Phone size={20} className="text-white" />
              </div>
              <div>
                <p className="font-heading uppercase tracking-[0.1em] text-sm text-[#1a2540] mb-1">
                  Phone
                </p>
                <a
                  href={`tel:${c.hq.phoneRaw}`}
                  className="text-sm text-[#6a6a6a] hover:text-[#1b75bc] transition-colors"
                >
                  {c.hq.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Business Areas */}
      <section className="py-20 bg-[#f6f8fa]">
        <div className="mx-auto max-w-[1240px] px-6">
          <div className="text-center mb-14">
            <p className="font-heading uppercase tracking-[0.35em] text-[#1b75bc] text-sm mb-3">
              What We Do
            </p>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540]">
              Core Business Areas
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {c.coreBusinessAreas.map((area, i) => {
              const Icon = iconMap[area.icon] || Droplets;
              return (
                <Reveal key={area.title} delay={i * 90}>
                <div className="group relative msv-card h-full p-8 overflow-hidden">
                  <span className="absolute left-0 top-0 h-1 w-full bg-transparent group-hover:bg-[#1b75bc] transition-colors duration-300" />
                  <div className="msv-icon w-14 h-14 mb-5 transition-transform duration-300 group-hover:scale-110">
                    <Icon size={26} />
                  </div>
                  <h3 className="font-heading text-lg uppercase tracking-[0.04em] text-[#1a2540] mb-3 leading-tight">
                    {area.title}
                  </h3>
                  <p className="text-sm text-[#6a6a6a] leading-relaxed">{area.desc}</p>
                </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-[#0f6b82]">
        <div className="mx-auto max-w-[900px] px-6 text-center">
          <p className="font-heading uppercase tracking-[0.35em] text-white/70 text-sm mb-4">
            Our Mission
          </p>
          <p className="font-heading text-xl md:text-2xl text-white leading-relaxed">
            {c.mission}
          </p>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="text-center mb-12">
            <p className="font-heading uppercase tracking-[0.35em] text-[#1b75bc] text-sm mb-3">
              Who We Serve
            </p>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540]">
              Industries We Serve
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.industries.map((industry, i) => {
              const Icon = industryIcons[industry] || Check;
              return (
                <Reveal key={industry} delay={i * 40}>
                <div className="group relative flex items-center gap-4 bg-white rounded-2xl border border-gray-100 p-5 overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:border-[#1b75bc]/30 hover:shadow-[0_20px_40px_-22px_rgba(15,41,74,0.4)] hover:-translate-y-1 transition-all duration-300">
                  {/* hover sweep */}
                  <span className="absolute inset-0 bg-gradient-to-br from-[#1b75bc]/[0.06] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-[#eef4fb] text-[#1b75bc] shrink-0 transition-all duration-300 group-hover:bg-gradient-to-br group-hover:from-[#1a2540] group-hover:to-[#0f2c4a] group-hover:text-[#4aa3e0] group-hover:scale-105">
                    <Icon size={22} />
                  </span>
                  <span className="relative font-heading text-sm uppercase tracking-[0.03em] text-[#1a2540] leading-snug">
                    {industry}
                  </span>
                  <ArrowRight
                    size={16}
                    className="relative ml-auto text-[#1b75bc] shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                  />
                </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Flagship Product Portfolio */}
      <section className="py-20 bg-[#f6f8fa]">
        <div className="mx-auto max-w-[1000px] px-6">
          <div className="text-center mb-14">
            <p className="font-heading uppercase tracking-[0.35em] text-[#1b75bc] text-sm mb-3">
              Flagship Products
            </p>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540]">
              Product Portfolio
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {c.portfolio.map((p, i) => {
              const Icon = iconMap[p.icon] || Droplets;
              return (
                <Reveal key={p.name} delay={i * 90}>
                <div className="group relative msv-card h-full p-8 overflow-hidden">
                  <span className="absolute left-0 top-0 h-full w-1 bg-transparent group-hover:bg-[#1b75bc] transition-colors duration-300" />
                  <div className="flex items-center gap-4 mb-4">
                    <div className="msv-icon w-12 h-12 shrink-0 transition-transform duration-300 group-hover:scale-110">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-heading text-lg uppercase tracking-[0.04em] text-[#1a2540] leading-tight">
                      {p.name}
                    </h3>
                  </div>
                  <p className="text-sm text-[#6a6a6a] leading-relaxed">{p.desc}</p>
                </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* The MSV Advantage */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-[1000px] px-6">
          <div className="text-center mb-14">
            <p className="font-heading uppercase tracking-[0.35em] text-[#1b75bc] text-sm mb-3">
              Why Choose Us
            </p>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540]">
              The Market Street Ventures Advantage
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {c.advantages.map((adv, i) => (
              <Reveal key={adv} delay={i * 60}>
              <div className="group flex items-start gap-4 bg-white rounded-xl border border-gray-100 px-6 py-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:shadow-[0_16px_32px_-18px_rgba(15,41,74,0.28)] hover:-translate-y-1 hover:border-[#1b75bc]/25 transition-all duration-400">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1b75bc] to-[#12507f] flex items-center justify-center shrink-0 shadow-[0_6px_12px_-4px_rgba(27,117,188,0.5)]">
                  <Check size={16} className="text-white" />
                </div>
                <span className="text-sm text-[#4a4a4a] leading-relaxed">{adv}</span>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 bg-[#1a2540]">
        <div className="mx-auto max-w-[860px] px-6 text-center">
          <h2 className="font-heading text-3xl uppercase tracking-[0.08em] text-white mb-6">
            {c.closing.heading}
          </h2>
          <p className="text-gray-300 leading-relaxed mb-10">{c.closing.text}</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.18em] text-sm text-white bg-[#1b75bc] px-10 py-4 hover:bg-[#155e97] transition-colors duration-300"
          >
            Partner With Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
